// v5: Expressions was removed, define URL regex inline
const URL_REGEX = /https?:\/\/[^\s]+/g;
import { Box, Flex, Text } from "@chakra-ui/react";
import { kinds, NostrEvent } from "nostr-tools";
import { useMemo } from "react";
import { Photo } from "react-photo-album";

import { isImageURL } from "../../../helpers/url";
import useEventIntersectionRef from "../../../hooks/use-event-intersection-ref";
import { useBreakpointValue } from "../../../providers/global/breakpoint-provider";
import { GalleryImage } from "../../content/components/gallery";
import { EmbeddedImageProps } from "../../content/links";
import { LightboxProvider } from "../../lightbox-provider";
import PhotoGallery, { PhotoWithoutSize } from "../../photo-gallery";
import UserAvatar from "../../user/user-avatar";
import UserName from "../../user/user-name";

function CustomGalleryImage({ event, ...props }: EmbeddedImageProps & { event: NostrEvent }) {
  const ref = useEventIntersectionRef<HTMLImageElement>(event);

  return (
    <Box position="relative" role="group">
      <GalleryImage {...props} event={event} ref={ref} />
      <Flex
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        bg="blackAlpha.600"
        px="2"
        py="1"
        gap="2"
        alignItems="center"
        opacity="0"
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s"
        pointerEvents="none"
      >
        <UserAvatar pubkey={event.pubkey} size="xs" />
        <Text color="white" fontSize="xs" isTruncated>
          <UserName pubkey={event.pubkey} />
        </Text>
      </Flex>
    </Box>
  );
}

type PhotoWithEvent = PhotoWithoutSize & { event: NostrEvent };
function ImageGallery({ images }: { images: PhotoWithEvent[] }) {
  const rowMultiplier = useBreakpointValue({ base: 2, sm: 3, md: 3, lg: 4, xl: 5 }) ?? 2;

  return (
    <PhotoGallery<Photo & { event: NostrEvent }>
      layout="masonry"
      photos={images}
      renderPhoto={({ photo, imageProps }) => (
        <CustomGalleryImage src={imageProps.src} event={photo.event} style={imageProps.style} />
      )}
      columns={rowMultiplier}
    />
  );
}

export default function MediaTimeline({ timeline }: { timeline: NostrEvent[] }) {
  const images = useMemo(() => {
    const images: PhotoWithEvent[] = [];

    for (const event of timeline) {
      if (event.kind === kinds.Repost || event.kind === kinds.GenericRepost) continue;
      const urls = event.content.matchAll(URL_REGEX);

      for (const match of urls) {
        if (isImageURL(match[0])) images.push({ event, src: match[0] });
      }
    }

    return images;
  }, [timeline]);

  return (
    <LightboxProvider>
      <ImageGallery images={images} />
    </LightboxProvider>
  );
}
